import type {
  ActiveSdrs,
  AdminClient,
  AdminPhoneNumber,
  AdminPool,
  AdminSdr,
  BoardData,
  PoolLeadRegion,
  SdrDirectoryEntry,
} from './api';
import { NANP_AREA_CODES, NANP_OTHER_COUNTRIES } from './nanp-area-codes';
import { JOINABLE_POOL_STATES } from '@/lib/pool-lifecycle';

// =====================================================================
// Pure board helpers — grouping, caller-ID readiness, color safety. Kept
// framework-free so the board logic ported from dialer-console.html is unit
// tested without a DOM (board-state.test.ts). The Board component consumes these.
// =====================================================================

export const DEFAULT_POOL_COLOR = '#174ea6';

// Accept only a hex color; fall back to the default (mirrors the original's
// bdSafeColor). Guards against a bad stored value reaching an inline style.
export function safeColor(c: string | null | undefined): string {
  return /^#[0-9a-f]{3,8}$/i.test(String(c ?? '')) ? (c as string) : DEFAULT_POOL_COLOR;
}

// Format a spend amount (micro-USD, i.e. millionths of a dollar) as a compact dollar
// string for a card badge. Sub-cent-but-nonzero shows "<$0.01" so a tiny early spend
// isn't rounded to "$0.00"; everything else is plain dollars-and-cents. A negative/NaN
// input floors to "$0.00". Used for both the LLM and telephony spend badges.
export function formatSpend(micros: number): string {
  const dollars = Number.isFinite(micros) ? micros / 1_000_000 : 0;
  if (dollars <= 0) return '$0.00';
  if (dollars < 0.01) return '<$0.01';
  return `$${dollars.toFixed(2)}`;
}

// Back-compat alias — the formatter is spend-source-agnostic, so new callers should
// prefer formatSpend.
export const formatLlmSpend = formatSpend;

// A phone number is "active" (usable as a caller ID) when its status is active.
export function isActiveNumber(n: AdminPhoneNumber): boolean {
  return String(n.status ?? 'active').toLowerCase() === 'active';
}

// A pool is "ready to dial" when at least one ACTIVE caller ID exists — anywhere.
//
// Not per-pool, and there is no pool argument to give it: numbers are one global
// inventory that every pool dials from, so "the caller IDs THIS pool can use" is the
// whole list. The predecessors (callerIdsForPool / callerIdPosition) filtered by
// `pool_ids` and sorted by a per-pool rotation `position`; both are gone from the
// core, and keeping them here would badge a perfectly dialable pool "not ready".
export function poolReady(numbers: AdminPhoneNumber[]): boolean {
  return numbers.some(isActiveNumber);
}

// The set of live (currently-calling) SDR ids across all pools, from the core
// runtime `active` map (pool id string -> [sdr id]).
export function liveSdrIds(data: Pick<BoardData, 'active'>): Set<number> {
  const set = new Set<number>();
  for (const ids of Object.values(data.active ?? {})) for (const id of ids) set.add(id);
  return set;
}

// A unified assignable-SDR entry the board renders. Sourced from the auth
// directory (real @throxy.com sign-ins) in prod, or the core SDR rows as a dev
// fallback (auth off). `email` is the assignment key when present; `sdrId` is the
// core row id when one exists (dev fallback + the unassign path). `pool_ids` are
// the pools this person is assigned to (from their core row, matched by email).
export interface SdrRosterEntry {
  key: string;
  name: string;
  email: string | null;
  sdrId: number | null;
  pool_ids: number[];
}

// Merge the auth directory with the core SDR rows into the assignable roster. In
// prod the directory (the real sign-ins) is the source, each enriched with its
// core row's assignments (matched by email); with NO directory (auth off) we fall
// back to the core SDRs so local dev still works. Deduped/keyed by email (lowered)
// in the directory case, or by `id:<n>` in the fallback.
export function buildSdrRoster(directory: SdrDirectoryEntry[], sdrs: AdminSdr[]): SdrRosterEntry[] {
  const byEmail = new Map<string, AdminSdr>();
  for (const s of sdrs) if (s.email) byEmail.set(s.email.toLowerCase(), s);

  if (directory.length > 0) {
    return directory.map((u) => {
      const s = byEmail.get(u.email.toLowerCase());
      return {
        key: u.email.toLowerCase(),
        name: u.name || u.email,
        email: u.email,
        sdrId: s?.id ?? null,
        pool_ids: s?.pool_ids ?? [],
      };
    });
  }
  return sdrs.map((s) => ({
    key: `id:${s.id}`,
    name: s.name,
    email: s.email,
    sdrId: s.id,
    pool_ids: s.pool_ids ?? [],
  }));
}

// Roster entries ASSIGNED to a pool — the admin-controlled roster shown per pool.
// Scopes each person's cockpit picker; cross-reference `callingSdrsInPool` for who's
// actually live.
export function assignedRosterInPool(roster: SdrRosterEntry[], poolId: number): SdrRosterEntry[] {
  return roster.filter((e) => e.pool_ids.includes(poolId));
}

// The reasons a draft pool can't be opened yet — one message per unmet requirement,
// empty when it's ready. A pool can only go live once it can actually dial someone:
// a caller ID to dial FROM, an SDR to answer, and a contact to dial TO. Pure so the
// board's Open gate is unit-tested; the board feeds it live counts.
export function poolOpenBlockers(counts: {
  callerIdCount: number;
  assignedSdrCount: number;
  contactCount: number;
}): string[] {
  const missing: string[] = [];
  if (counts.callerIdCount === 0)
    missing.push('a caller ID to dial from — buy one on the Numbers screen');
  if (counts.assignedSdrCount === 0) missing.push('at least one assigned SDR — use “Assign SDRs”');
  if (counts.contactCount === 0) missing.push('at least one contact to dial');
  return missing;
}

// =====================================================================
// Redial no-pickups — the copy + gate for another pass over a pool.
// Pure (and here rather than in Board.tsx) because vitest is node-only in this
// app: .tsx can't be tested, so every sentence the modal renders is built here.
// =====================================================================

// Mirrors the core's rename rule (Pool.rename_changeset/2) so a rename can be
// rejected without a round-trip. The core stays authoritative — it sees pools this
// board hasn't polled yet, so its 422 is still rendered.
export const MIN_POOL_NAME_LENGTH = 4;

// Why this name can't be used, or null when it can. A name equal to the current one
// is accepted: the caller treats "no change" as a silent exit, not an error.
export function validatePoolName(
  next: string,
  current: string,
  otherNames: string[],
): string | null {
  const trimmed = next.trim();
  if (trimmed.length < MIN_POOL_NAME_LENGTH)
    return `Pool name must be at least ${MIN_POOL_NAME_LENGTH} characters.`;
  if (trimmed === current) return null;
  if (otherNames.some((n) => n.trim().toLowerCase() === trimmed.toLowerCase()))
    return 'Another pool already has that name.';
  return null;
}

// Offered on closed AND live pools — the core re-arms a running pool's released
// contacts in place, so a pool that keeps growing never has to close just to
// redial. Only `draft` (never dialed anything) and `draining` (mid-close, racing
// its evictions) are out, matching the core's 422 gate.
export function canRedialPool(pool: Pick<AdminPool, 'run_state'>): boolean {
  return pool.run_state !== 'draft' && pool.run_state !== 'draining';
}

// What a closed pool could dial right now if it were open; null when it is not closed.
// 0 is a real count here — a spent pool — so it cannot double as "does not apply".
export function closedPoolDialable(
  pool: Pick<AdminPool, 'run_state' | 'dialable_including_claimed'>,
): number | null {
  return pool.run_state === 'closed' ? (pool.dialable_including_claimed ?? 0) : null;
}

// One-line "who gets redialled" summary, e.g. "Voicemail 24 · AI Gate 13". The core
// already sorted `by_disposition` (count desc, then label), so the order is kept
// as-is. Empty string when there is nothing to redial (the modal shows its empty
// explanation instead).
export function redialBreakdownSummary(rows: readonly { label: string; count: number }[]): string {
  return rows.map((r) => `${r.label} ${r.count}`).join(' · ');
}

// Confirmation copy for the toast after an applied redial. `requeued` is what the
// core actually moved, which can be 0 if the dispositions changed between the
// preview and the click.
export function redialAppliedMessage(requeued: number): string {
  if (requeued <= 0) return 'Nothing to redial — no contacts were requeued.';
  return `Requeued ${requeued} contact${requeued === 1 ? '' : 's'} · the pool is open again for a second pass.`;
}

// Who is let in, not who can dial right now: calling hours are not on these rows, so a
// pool asleep in its own window still counts.
export function countPoolsOpenToAll(pools: readonly AdminPool[]): number {
  return pools.filter(
    (p) =>
      p.open_to_all &&
      !p.hidden &&
      (JOINABLE_POOL_STATES as readonly string[]).includes(p.run_state),
  ).length;
}

// Confirmation copy after a floor-wide access change. Restricting reports the SDRs it
// moved — the part with a human on the other end — rather than the row count, most of
// which is pools nobody could have joined anyway. Opening skips hidden pools, so the
// wording must not claim the whole board.
export function poolAccessResultMessage(openToAll: boolean, evicted: number): string {
  if (openToAll) return 'Every pool that isn’t hidden is now open to every SDR.';
  const moved =
    evicted > 0
      ? `${evicted} SDR${evicted === 1 ? '' : 's'} sent back to the picker.`
      : 'Nobody needed moving.';
  return `Restricted every pool to its assigned SDRs. ${moved}`;
}

// SDRs the core runtime reports as PRESENT in this pool right now — whoever has
// JOINED via the cockpit and is idle/on a call/wrapping up — resolved against the
// roster for names. Sourced from the `active` map (pool id -> [sdr id]), which the
// board polls, so it tracks joins/leaves live. This is the operational "calling
// now" view, distinct from `assignedSdrsInPool` (the static admin roster). Drops
// any active id with no matching SDR row.
export function callingSdrsInPool(
  sdrs: AdminSdr[],
  active: ActiveSdrs,
  poolId: number,
): AdminSdr[] {
  const present = new Set(active[poolId] ?? []);
  return sdrs.filter((s) => present.has(s.id));
}

// =====================================================================
// Console filtering — pure predicates over a pool + the current filter state.
// Kept here (framework-free, tested in board-state.test.ts) so the Board only
// owns the filter UI state; the matching logic is unit-tested without a DOM.
// =====================================================================

// Every lifecycle state is individually selectable, so an operator can isolate
// e.g. only the paused pools. Hand-mirrors the core's run_state list (shared's
// PoolRunState isn't imported into the SPAs by convention — see cockpit/protocol.ts).
export type PoolStateFilter =
  | 'all'
  | 'calling'
  | 'open'
  | 'paused'
  | 'draining'
  | 'draft'
  | 'closed';

export function poolMatchesState(pool: AdminPool, state: PoolStateFilter): boolean {
  return state === 'all' || pool.run_state === state;
}

// A pool matches the SDR filter when the selected person is ASSIGNED to it or is
// CURRENTLY CALLING in it. `entry` is the chosen roster entry (null = "All SDRs").
export function poolMatchesSdr(
  pool: AdminPool,
  entry: SdrRosterEntry | null,
  active: ActiveSdrs,
): boolean {
  if (!entry) return true;
  const assigned = entry.pool_ids.includes(pool.id);
  const calling = entry.sdrId != null && (active[pool.id] ?? []).includes(entry.sdrId);
  return assigned || calling;
}

// Matches when the pool's effective (majority) client is the selected one. Pools have
// no client column, so this reads the `effective_client` that list_pools resolves.
export function poolMatchesClient(pool: AdminPool, clientId: number | null): boolean {
  if (clientId == null) return true;
  return pool.effective_client?.id === clientId;
}

export interface PoolFilters {
  query: string; // raw text from the search box
  state: PoolStateFilter;
  sdrKey: string | null; // SdrRosterEntry.key, or null for "All SDRs"
  client: number | null; // AdminClient.id, or null for "All clients"
}

// Apply all four console filters (AND) over the pool list, preserving input order.
export function filterPools(
  pools: AdminPool[],
  filters: PoolFilters,
  ctx: { roster: SdrRosterEntry[]; active: ActiveSdrs },
): AdminPool[] {
  const q = filters.query.trim().toLowerCase();
  const entry = filters.sdrKey ? (ctx.roster.find((e) => e.key === filters.sdrKey) ?? null) : null;
  return pools.filter(
    (p) =>
      (q === '' || p.name.toLowerCase().includes(q)) &&
      poolMatchesState(p, filters.state) &&
      poolMatchesSdr(p, entry, ctx.active) &&
      poolMatchesClient(p, filters.client),
  );
}

// Contacts still to dial — the exact Postgres count the core sends (`dialed_at IS
// NULL`), the same basis as the card's "N to dial" and the cockpit. `Math.max` is
// belt-and-braces against a transient poll where the fields disagree.
export function undialledLeads(pool: AdminPool): number {
  return Math.max(0, pool.remaining_count);
}

export type PoolSort = 'active' | 'newest' | 'name' | 'undialled';

// Default-ordering tier: pools that need attention float up, dormant ones sink.
// Lower rank = higher in the list.
//   0  open / calling  — actively dialing (or ready to), the operator's focus
//   0  calling — the dialer is in `calling`, or an SDR is live on a call in the
//      pool right now (the "Calling now" chips); calls are actually happening
//   1  open    — ready to dial, nothing live yet
//   2  paused / draft / draining — active but not currently dialing
//   3  closed  — done
//   4  hidden  — deliberately tucked away (any state), always last
function poolActivityRank(pool: AdminPool, active: ActiveSdrs): number {
  if (pool.hidden) return 4;
  if (pool.run_state === 'closed') return 3;
  if (pool.run_state === 'calling' || (active[pool.id] ?? []).length > 0) return 0;
  if (pool.run_state === 'open') return 1;
  return 2;
}

// created_at as epoch ms, tolerant of the null/blank value pools predating
// created-at tracking carry — those sort last within their tier rather than first.
function poolCreatedAtMs(pool: AdminPool): number {
  const t = pool.created_at ? Date.parse(pool.created_at) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

// Sorts a copy — the caller's array is the board's own pool list.
// - 'active' (default): actually-calling pools first (run_state `calling` or a
//   live SDR in the pool, via `active`), then open, then paused/draft/draining,
//   then closed, then hidden — so the pools being worked stay up top;
//   newest-created first within each tier.
// - 'newest': every pool by creation, most recent first, ignoring activity tier.
// - 'undialled': descending (most work remaining first).
// Name is the final tie-break in every mode so the order doesn't shuffle on a
// refresh when pools share a key.
export function sortPools(
  pools: AdminPool[],
  sort: PoolSort,
  active: ActiveSdrs = {},
): AdminPool[] {
  const byName = (a: AdminPool, b: AdminPool) => a.name.localeCompare(b.name);
  const byNewest = (a: AdminPool, b: AdminPool) => poolCreatedAtMs(b) - poolCreatedAtMs(a);
  if (sort === 'name') return [...pools].sort(byName);
  if (sort === 'undialled')
    return [...pools].sort((a, b) => undialledLeads(b) - undialledLeads(a) || byName(a, b));
  if (sort === 'newest') return [...pools].sort((a, b) => byNewest(a, b) || byName(a, b));
  return [...pools].sort(
    (a, b) =>
      poolActivityRank(a, active) - poolActivityRank(b, active) || byNewest(a, b) || byName(a, b),
  );
}

// The org-slug shape Throxy mints: lowercase alphanumerics in hyphen-separated
// runs, ending in a short discriminator (`ampup-574d`). Deliberately loose — it
// rejects paste damage (spaces, capitals, a trailing comma, a pasted URL) rather
// than trying to validate the discriminator, which only Throxy can confirm.
const THROXY_CLIENT_ID_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export interface ThroxyClientIdCheck {
  // Normalized id — what to POST, so a stray capital or space can't mint a
  // second client that matches nothing downstream.
  slug: string;
  // The client already holding this id, if any.
  clash: AdminClient | null;
  // Non-empty but not slug-shaped.
  malformed: boolean;
  // Safe to submit (non-empty, well-formed, unused).
  ok: boolean;
}

// Pre-flight for the Create client form. The clash check is convenience — the
// partial-unique index would 422 anyway; this just names the owner without a
// round trip. The shape check is the one that matters: a typo'd but UNIQUE id
// (`ampup-574` for `ampup-574d`) inserts cleanly and then fails silently
// forever, because it's leadflow's match key and the org_id inbound DNC events
// join on. Nothing downstream would report the mismatch.
export function checkThroxyClientId(
  raw: string,
  clients: readonly AdminClient[],
): ThroxyClientIdCheck {
  const slug = raw.trim().toLowerCase();
  if (slug === '') return { slug, clash: null, malformed: false, ok: false };

  const clash = clients.find((c) => c.throxy_client_id?.toLowerCase() === slug) ?? null;
  const malformed = !THROXY_CLIENT_ID_RE.test(slug);
  return { slug, clash, malformed, ok: !clash && !malformed };
}

// =====================================================================
// Caller-ID region grouping — derive a geographic bucket from a number's E.164
// string so the caller-ID picker can collapse long lists into scannable, count-
// badged sections. There's no region on the wire (the core doesn't send one), so
// we derive it here. Pure + framework-free; tested in board-state.test.ts.
// =====================================================================

// A geographic bucket a caller ID belongs to. `key` is the stable identity used
// for grouping + collapse state; `label`/`flag` are display-only.
export interface CallerRegion {
  key: string; // 'US-CA' | 'CA' | 'GB' | 'unknown' …
  label: string; // 'California · CA' | 'Canada' | 'United Kingdom' | 'Other'
  flag: string; // 🇺🇸 | 🇨🇦 | 🇬🇧 | 🏳️
}

export interface CallerRegionGroup {
  region: CallerRegion;
  numbers: AdminPhoneNumber[]; // input order preserved within the group
  count: number; // === numbers.length
}

const US_FLAG = '🇺🇸';
const UNKNOWN_REGION: CallerRegion = { key: 'unknown', label: 'Other', flag: '🏳️' };

// Non-NANP countries by E.164 dial-code prefix. Longest prefix wins (so +353
// beats +3…). Seeded with the numbers we actually hold (UK, Belgium) plus common
// EU/RoW codes so real inventory doesn't collapse into one big "Other" — cheap to
// extend, an unlisted code just lands in the "Other" bucket.
const COUNTRY_BY_DIAL_CODE: Record<string, CallerRegion> = {
  '+44': { key: 'GB', label: 'United Kingdom', flag: '🇬🇧' },
  '+32': { key: 'BE', label: 'Belgium', flag: '🇧🇪' },
  '+33': { key: 'FR', label: 'France', flag: '🇫🇷' },
  '+49': { key: 'DE', label: 'Germany', flag: '🇩🇪' },
  '+34': { key: 'ES', label: 'Spain', flag: '🇪🇸' },
  '+39': { key: 'IT', label: 'Italy', flag: '🇮🇹' },
  '+31': { key: 'NL', label: 'Netherlands', flag: '🇳🇱' },
  '+351': { key: 'PT', label: 'Portugal', flag: '🇵🇹' },
  '+353': { key: 'IE', label: 'Ireland', flag: '🇮🇪' },
  '+41': { key: 'CH', label: 'Switzerland', flag: '🇨🇭' },
  '+43': { key: 'AT', label: 'Austria', flag: '🇦🇹' },
  '+45': { key: 'DK', label: 'Denmark', flag: '🇩🇰' },
  '+46': { key: 'SE', label: 'Sweden', flag: '🇸🇪' },
  '+47': { key: 'NO', label: 'Norway', flag: '🇳🇴' },
  '+48': { key: 'PL', label: 'Poland', flag: '🇵🇱' },
  '+52': { key: 'MX', label: 'Mexico', flag: '🇲🇽' },
  '+55': { key: 'BR', label: 'Brazil', flag: '🇧🇷' },
  '+61': { key: 'AU', label: 'Australia', flag: '🇦🇺' },
  '+64': { key: 'NZ', label: 'New Zealand', flag: '🇳🇿' },
  '+65': { key: 'SG', label: 'Singapore', flag: '🇸🇬' },
  '+91': { key: 'IN', label: 'India', flag: '🇮🇳' },
  '+27': { key: 'ZA', label: 'South Africa', flag: '🇿🇦' },
  '+852': { key: 'HK', label: 'Hong Kong', flag: '🇭🇰' },
  '+971': { key: 'AE', label: 'United Arab Emirates', flag: '🇦🇪' },
  '+972': { key: 'IL', label: 'Israel', flag: '🇮🇱' },
};

// Best-effort normalize to E.164 ('+' + digits). Accepts the formatting the core
// might hand us (spaces, dashes, parens) and bare NANP numbers (10 digits, or 11
// with a leading 1). Returns '' when there's nothing to work with.
function toE164(raw: string): string {
  const cleaned = String(raw ?? '').replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (/^1\d{10}$/.test(cleaned)) return `+${cleaned}`; // 1XXXXXXXXXX → +1…
  if (/^\d{10}$/.test(cleaned)) return `+1${cleaned}`; // bare 10-digit NANP → +1…
  return cleaned ? `+${cleaned}` : '';
}

// The country region for a non-NANP E.164 number, by longest matching dial code.
function countryRegion(e164: string): CallerRegion | null {
  let best: CallerRegion | null = null;
  let bestLen = 0;
  for (const [code, region] of Object.entries(COUNTRY_BY_DIAL_CODE)) {
    if (e164.startsWith(code) && code.length > bestLen) {
      best = region;
      bestLen = code.length;
    }
  }
  return best;
}

// Derive a caller ID's region from its number. '+1' NANP numbers resolve by area
// code to a US state (or Canada); other numbers resolve by dial-code prefix to a
// country. Anything unrecognized falls into the "Other" bucket — never throws.
export function regionForNumber(raw: string): CallerRegion {
  const e164 = toE164(raw);
  if (!e164) return UNKNOWN_REGION;

  if (e164.startsWith('+1') && e164.length >= 5) {
    const areaCode = e164.slice(2, 5);
    const entry = NANP_AREA_CODES[areaCode];
    // Note: `country: 'CA'` means Canada; California is a US state whose code is
    // also 'CA' — they stay distinct at the region-key layer ('CA' vs 'US-CA').
    if (entry?.country === 'US' && entry.state && entry.name) {
      return { key: `US-${entry.state}`, label: `${entry.name} · ${entry.state}`, flag: US_FLAG };
    }
    if (entry?.country === 'CA') return { key: 'CA', label: 'Canada', flag: '🇨🇦' };
    // A sovereign non-US/CA nation on the +1 plan (Caribbean/Atlantic) — its own
    // country region, not a US bucket.
    const other = NANP_OTHER_COUNTRIES[areaCode];
    if (other) return other;
    // A +1 number with no known geographic area code (unassigned, toll-free, or
    // premium) — still clearly US-plan, just not placeable to a state.
    return { key: 'US-unknown', label: 'United States · other', flag: US_FLAG };
  }

  return countryRegion(e164) ?? UNKNOWN_REGION;
}

// Bucket numbers into region groups, sorted by count desc then label asc (biggest
// pools surface first). Order within each group is the input order — the caller
// pre-sorts (e.g. by dial position or number string). Empty in → empty out.
export function groupCallerIdsByRegion(numbers: AdminPhoneNumber[]): CallerRegionGroup[] {
  const groups = new Map<string, CallerRegionGroup>();
  for (const n of numbers) {
    const region = regionForNumber(n.number);
    const existing = groups.get(region.key);
    if (existing) existing.numbers.push(n);
    else groups.set(region.key, { region, numbers: [n], count: 0 });
  }
  const out = [...groups.values()];
  for (const g of out) g.count = g.numbers.length;
  out.sort((a, b) => b.count - a.count || a.region.label.localeCompare(b.region.label));
  return out;
}

// =====================================================================
// Lead-region coverage — joins the core's per-pool lead-region counts against
// the caller-ID region groups so the manage modal can float relevant number
// groups above a "no leads in these regions" divider. Lead keys come from the
// core's Region module, which mirrors the datasets above, so the two sides
// bucket identically.
// =====================================================================

// Display info for a lead-region key, built from the same datasets the number
// grouping uses. Keys without a display entry (an unlisted calling code) fall
// back to the raw key.
const buildLeadRegionDisplay = (): Record<string, CallerRegion> => {
  const map: Record<string, CallerRegion> = {
    CA: { key: 'CA', label: 'Canada', flag: '🇨🇦' },
    'US-unknown': { key: 'US-unknown', label: 'United States · other', flag: US_FLAG },
    unknown: UNKNOWN_REGION,
  };
  for (const e of Object.values(NANP_AREA_CODES)) {
    if (e.country === 'US' && e.state && e.name) {
      const key = `US-${e.state}`;
      map[key] = { key, label: `${e.name} · ${e.state}`, flag: US_FLAG };
    }
  }
  for (const c of Object.values(NANP_OTHER_COUNTRIES)) map[c.key] = c;
  for (const r of Object.values(COUNTRY_BY_DIAL_CODE)) map[r.key] = r;
  return map;
};

const LEAD_REGION_DISPLAY = buildLeadRegionDisplay();

export function leadRegionDisplay(key: string): CallerRegion {
  return LEAD_REGION_DISPLAY[key] ?? { key, label: key, flag: '🏳️' };
}

// The country a region key belongs to ('US-CA' → 'US'); null when unplaceable.
function countryOfKey(key: string): string | null {
  if (key === 'unknown') return null;
  if (key.startsWith('US-')) return 'US';
  return key;
}

// Whether a number in this region is a plausible caller ID for any of the
// leads (shares a country). The pinned primary sits outside the region groups,
// so the modal checks it with this directly.
export function regionCoversLeads(key: string, leads: PoolLeadRegion[]): boolean {
  const country = countryOfKey(key);
  if (!country) return false;
  return leads.some((l) => countryOfKey(l.key) === country);
}

// Whether any lead sits in a region a number could match. All-unplaceable
// leads make a coverage split meaningless — nothing can float.
export function hasPlaceableLeads(leads: PoolLeadRegion[]): boolean {
  return leads.some((l) => countryOfKey(l.key) !== null);
}

// Example dial prefix for a region key ('GB' → '+44', 'US-CA' → '+1'), so the
// coverage copy can show a concrete mismatch. Null when the key has no known
// prefix.
export function dialPrefixForKey(key: string): string | null {
  const country = countryOfKey(key);
  if (!country) return null;
  if (country === 'US' || country === 'CA') return '+1';
  if (Object.values(NANP_OTHER_COUNTRIES).some((c) => c.key === country)) return '+1';
  for (const [code, region] of Object.entries(COUNTRY_BY_DIAL_CODE)) {
    if (region.key === country) return code;
  }
  return null;
}

// A concrete "🇬🇧 +44" example for the coverage copy; null when the key has no
// known dial prefix.
export function regionExample(key: string, flag: string): string | null {
  const prefix = dialPrefixForKey(key);
  return prefix ? `${flag} ${prefix}` : null;
}

export function firstLeadExample(leads: PoolLeadRegion[]): string | null {
  for (const l of leads) {
    const example = regionExample(l.key, leadRegionDisplay(l.key).flag);
    if (example) return example;
  }
  return null;
}

export interface CoverageSplit {
  matching: Array<{ group: CallerRegionGroup; leadCount: number }>;
  other: CallerRegionGroup[];
}

// Split number groups by whether their region shares a country with any lead.
// Exact-key groups carry that region's lead count for the header badge; a
// same-country group with no leads in its exact region still floats (a Nevada
// number is a reasonable pick for a pool of Texas leads).
export function splitGroupsByLeadCoverage(
  groups: CallerRegionGroup[],
  leads: PoolLeadRegion[],
): CoverageSplit {
  const leadsByKey = new Map(leads.map((l) => [l.key, l.count]));
  const leadCountries = new Set<string>();
  for (const l of leads) {
    const country = countryOfKey(l.key);
    if (country) leadCountries.add(country);
  }

  const matching: CoverageSplit['matching'] = [];
  const other: CallerRegionGroup[] = [];
  for (const group of groups) {
    const country = countryOfKey(group.region.key);
    if (country && leadCountries.has(country)) {
      matching.push({ group, leadCount: leadsByKey.get(group.region.key) ?? 0 });
    } else {
      other.push(group);
    }
  }
  matching.sort(
    (a, b) =>
      b.leadCount - a.leadCount ||
      b.group.count - a.group.count ||
      a.group.region.label.localeCompare(b.group.region.label),
  );
  return { matching, other };
}

export interface LeadCoverageLine {
  flag: string;
  label: string;
  count: number;
  detail: string | null;
}

// Country-level rollup for the modal's one-line "who are we calling" summary,
// count-desc, with the top US states inlined (e.g. "TX 9, PA 12").
export function leadCoverageSummary(leads: PoolLeadRegion[]): LeadCoverageLine[] {
  const byCountry = new Map<string, { count: number; subs: PoolLeadRegion[] }>();
  for (const l of leads) {
    const country = countryOfKey(l.key) ?? 'unknown';
    const entry = byCountry.get(country) ?? { count: 0, subs: [] };
    entry.count += l.count;
    if (l.key.startsWith('US-') && l.key !== 'US-unknown') entry.subs.push(l);
    byCountry.set(country, entry);
  }

  return [...byCountry.entries()]
    .map(([country, { count, subs }]) => {
      const display =
        country === 'US' ? { label: 'United States', flag: US_FLAG } : leadRegionDisplay(country);
      const top = [...subs].sort((a, b) => b.count - a.count).slice(0, 3);
      const detail = top.length ? top.map((s) => `${s.key.slice(3)} ${s.count}`).join(', ') : null;
      return { flag: display.flag, label: display.label, count, detail };
    })
    .sort((a, b) => b.count - a.count);
}
